import AddCommentIcon from "@mui/icons-material/AddComment";
import MarkEmailRead from "@mui/icons-material/MarkEmailRead";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import LoadingButton from "_shared/_views/loading/loading-button";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import { messagingApi } from "messaging/_api/messaging-api";
import {
  addComment,
  getCommentsByProposal,
  markProposalCommentsAsRead,
  postComment,
  removeComment,
} from "work/proposals/comments/_store/comments-redux-slice";
import {
  composerDraftKey,
  proposalUncommittedInputScopeKey,
  readComposerDraft,
  writeComposerDraft,
} from "work/proposals/_store/proposal-uncommitted-input-store";
import Comment from "work/proposals/comments/comment";
import AudienceSwitch from "work/proposals/comments/_view/audience-switch";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { addCommentThread } from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import { proposalCommentPostButtonLabel } from "work/proposals/comments/proposal-comment-publishing";
import Proposal from "work/proposals/proposal";

const PostCommentInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    "& fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.common.black,
    },
  },
  "& .MuiFormHelperText-root": {
    margin: 0,
  },
}));
// The audience switch and Mark all read share a line where one fits, and take one each where it does
// not. Without the wrap both kept their places and broke their own labels across two lines instead,
// which is what a comments panel that narrows with the window turns a tight fit into.
const PostOptionsContainer = styled("span")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  paddingTop: theme.spacing(1),
}));
const MarkAllAsReadButton = styled(Button)(() => ({
  marginLeft: "auto",
  whiteSpace: "nowrap",
}));
/** Why the composer is refusing a comment, said where the audience switch says who a comment is for. */
const ComposerPausedNote = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "inline-block",
}));
const HeaderActions = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  paddingTop: theme.spacing(1),
}));

type CommentComposerProps = {
  proposal: Proposal;
  commentThreads: CommentThread[];
  isSaving?: boolean;
  readOnly?: boolean;
  showMarkAllRead?: boolean;
  /** Called once the comment has been added optimistically, so a host can scroll to it. */
  onCommentPosted?: () => void;
};

/**
 * Writes a comment onto the negotiation's conversation.
 *
 * Extracted from the comment list because the Comments/Changes pane shows the conversation and the
 * change history as one stream (the timeline), and still has to offer somewhere to write. The archive's
 * read-only comment list keeps the same header, which is why the mark-all-read affordance lives here
 * rather than beside the list.
 */
export default function CommentComposer(props: Readonly<CommentComposerProps>) {
  const { proposal, commentThreads } = props;
  const isSaving = props.isSaving ?? false;
  const readOnly = props.readOnly ?? false;
  const showMarkAllRead = props.showMarkAllRead ?? true;

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const [isExternal, setIsExternal] = React.useState(false);
  // Tracks the proposal whose unread comments were already marked read by an in-pane interaction, so
  // the interaction trigger fires at most once per open negotiation rather than on every keystroke.
  const interactionMarkedProposalIdRef = useRef<string | undefined>(undefined);

  // The in-progress comment is mirrored to the durable uncommitted-input store as it is typed (S4). This
  // pane is mounted under the page's proposal, so a render where the envelope blinks — a refetch, a
  // viewer-scope change, an Ably-driven replacement — unmounts it, and local state alone would take the
  // user's half-written comment with it; a full page reload would too, which is why the mirror is not
  // just an in-memory map. It is deliberately NOT Redux: a store write per keystroke re-runs every
  // subscriber on this page, which made typing crawl — writes go straight to a module-level Map
  // (mirrored to localStorage) instead, with zero subscribers to notify.
  const composerScopeKey = proposalUncommittedInputScopeKey(session);
  // The key this composer's draft is stored under. `undefined` means the proposal is momentarily
  // UNIDENTIFIABLE — mid-refetch, mid-viewer-scope-change, mid-Ably-replacement — not that this is a
  // composer for something else.
  //
  // Those two used to be the same value here: `composerDraftKey(proposal.id) ?? ""` turned an
  // unusable id into a key like any other, so a blink flipped the key to "" and back, and the
  // re-seeding below ran on the way out AND on the way back in. It re-read the store and replaced
  // whatever the author had typed since — while every keystroke inside that window went nowhere, the
  // store needing a key to write under. What came back was therefore the draft as it stood BEFORE the
  // author started typing. For a composer whose previous post had been refused that is the refused
  // comment, restored into the box and then posted, under a fresh id, in place of what they had
  // actually just written. Re-seeding is for a composer that has lost its state, never for one that
  // still has it.
  const composerKey = composerDraftKey(proposal.id);
  const [composer, setComposer] = React.useState<{ key?: string; text: string }>(() => ({
    key: composerKey,
    text: readComposerDraft(composerScopeKey, proposal.id),
  }));
  // Only ever seeds a key this composer is not already holding, and never on the way through an
  // unidentifiable render. Switching to a different proposal still re-seeds, which is what this is for.
  if (composerKey !== undefined && composer.key !== composerKey) {
    setComposer({ key: composerKey, text: readComposerDraft(composerScopeKey, proposal.id) });
  }
  const commentText = composer.text;
  const setCommentText = useCallback(
    (text: string) => {
      // Keeps the key it is already holding while the proposal cannot be named, so text typed in that
      // window is not mistaken afterwards for a draft belonging to some other composer.
      setComposer((current) => ({ key: composerKey ?? current.key, text }));
      writeComposerDraft(composerScopeKey, proposal.id, text);
    },
    [composerKey, composerScopeKey, proposal.id],
  );

  const proposalComments = useSelector(getCommentsByProposal(proposal.id));
  // `isUnreadBy` rather than the receipt check alone: a tombstone carries no read receipts, so it
  // answered "unread" for ever and left Mark all read enabled on a comment that no longer exists —
  // and pressing it wrote read receipts at deleted rows the server discards, so the button never
  // cleared. See `Comment.isUnreadBy`.
  const unreadComments = useMemo(
    () => (proposalComments ?? []).filter((comment) => comment.isUnreadBy(session.user?.id)),
    [proposalComments, session.user?.id],
  );
  const hasUnreadComments = unreadComments.length > 0;

  /**
   * Whether a comment may be posted at all right now.
   *
   * The one place the composer decides that, because it has two ways in — the Post button and the Enter
   * key — and they used to disagree: `isSaving` reached the button (as its spinner, which disables it)
   * and nothing else, so Enter went on posting comments through a composer that was showing itself as
   * unavailable. See #5124.
   *
   * A comment posted while the proposal is saving also lands ahead of the changes that save is still
   * writing, so the pane reads the conversation out of order — a comment about a change, above the
   * change it is about.
   */
  const isPostingPaused = isSaving;

  function handleCommentPosted() {
    if (readOnly || isPostingPaused) return;

    const comment = composeComment(commentText, isExternal);
    if (!comment) {
      console.error("Failed to compose comment");
      return;
    }

    // Optimistically show the comment, then immediately persist it — published to its audience by
    // default (see proposalCommentsPublishOnPost). It carries its own id, so the server round-trip
    // upserts the same row rather than duplicating it.
    dispatch(addCommentThread(comment.thread));
    dispatch(addComment(comment));
    // Failure is reported rather than dropped: the comment is on screen either way, so a silent failure
    // reads exactly like a successful post. Nothing retries it — there is no save left for it to ride
    // on — so a post that did not land takes its optimistic row back out and returns the text to the
    // composer, rather than leaving the author looking at a comment nobody received.
    void dispatch(postComment({ session, comment }))
      .unwrap()
      .catch((error: unknown) => {
        console.error("Failed to post comment", error);
        dispatch(removeComment(comment));
        setCommentText(comment.content);
        setIsExternal(comment.thread.isExternal);
        enqueueSnackbar("We couldn't post your comment. Please try again.", { variant: "error" });
      });
    setCommentText("");
    setIsExternal(false);
    props.onCommentPosted?.();
  }

  function composeComment(commentText: string, isExternal: boolean): Comment | undefined {
    if (!proposal?.id || !session.user?.id) return;

    return new Comment({
      // A proposal has one thread per audience and the server get-or-creates it, so the comment names
      // its audience and lets the server resolve the thread rather than guessing at a forum id.
      thread: CommentThread.forProposal({
        proposal,
        isViewingAsVendor: session.context?.viewingAsVendor ?? false,
        isExternal,
      }),
      content: commentText,
      senderId: session.user.id,
    });
  }

  async function handleMarkAllAsRead() {
    if (!unreadComments.length) {
      console.warn("No unread comments to mark as read.");
      return;
    }
    try {
      const unreadMessages = unreadComments
        .map((comment) => comment.markForReadReceipt())
        .map((comment) =>
          comment.toMessage({
            isViewingAsVendor: session.context?.viewingAsVendor ?? false,
          }),
        );
      const returnedMessages = await dispatch(
        messagingApi.endpoints.createBulkMessages.initiate({ messages: unreadMessages }),
      ).unwrap();
      if (!returnedMessages) {
        console.error("Failed to mark all comments as read.");
        return;
      }
      returnedMessages.forEach((message) => {
        const returnedComment = Comment.fromMessage(message);
        dispatch(addComment(returnedComment));
        dispatch(addCommentThread(returnedComment.thread));
      });
    } catch (error) {
      console.error("Error marking all comments as read:", error);
    }
  }

  // D10: Once the user actively engages the open pane (focusing or typing in the composer), treat the
  // conversation as seen and mark every unread comment read via the same markProposalCommentsAsRead the
  // open path dispatches. This deliberately also clears comments that arrived over websocket after the
  // pane opened (which the passive open path leaves unread). Fires at most once per open negotiation and
  // no-ops when nothing is unread, so it never dispatches per keystroke. The guard is only armed after a
  // real dispatch, so a focus while nothing is unread doesn't suppress a later interaction once an
  // unread comment shows up.
  function markOpenThreadsReadOnInteraction() {
    if (!proposal.id || interactionMarkedProposalIdRef.current === proposal.id.value) return;

    const hasUnreadThreadMessages = commentThreads.some((thread) => (thread.numUnreadMessages ?? 0) > 0);
    if (!hasUnreadComments && !hasUnreadThreadMessages) return;

    interactionMarkedProposalIdRef.current = proposal.id.value;
    void dispatch(
      markProposalCommentsAsRead({
        session,
        proposalId: proposal.id,
        commentThreads,
      }),
    );
  }

  const markAllReadButton = showMarkAllRead && (
    <MarkAllAsReadButton
      color="primary"
      size="small"
      disabled={!hasUnreadComments}
      startIcon={<MarkEmailRead />}
      variant="outlined"
      onClick={handleMarkAllAsRead}
    >
      Mark all read
    </MarkAllAsReadButton>
  );

  if (readOnly) {
    return <HeaderActions>{markAllReadButton}</HeaderActions>;
  }

  return (
    <PostCommentInput
      placeholder="Type your comment here..."
      multiline
      rows={4}
      variant="outlined"
      fullWidth
      // Locked while the proposal saves, rather than merely unpostable: a composer that takes the
      // comment and then refuses to send it is the same silence Enter used to slip through.
      disabled={isPostingPaused}
      value={commentText}
      helperText={
        <PostOptionsContainer>
          {isPostingPaused ? (
            <ComposerPausedNote data-testid="comment-composer-paused-note">
              Saving the proposal. You can comment again in a moment.
            </ComposerPausedNote>
          ) : (
            <AudienceSwitch
              proposal={proposal}
              isExternal={isExternal}
              onIsExternalChanged={(nextIsExternal) => setIsExternal(nextIsExternal)}
            />
          )}
          {markAllReadButton}
        </PostOptionsContainer>
      }
      slotProps={{
        input: {
          endAdornment: (
            <LoadingButton
              color="primary"
              endIcon={<AddCommentIcon />}
              disabled={commentText.trim() === ""}
              loading={isSaving}
              onClick={handleCommentPosted}
            >
              {proposalCommentPostButtonLabel}
            </LoadingButton>
          ),
        },
      }}
      onFocus={markOpenThreadsReadOnInteraction}
      onKeyDown={(event) => {
        if (event.key === KeyboardKey.Enter && commentText.trim() !== "") {
          event.preventDefault();
          handleCommentPosted();
        }
      }}
      onChange={(event) => {
        markOpenThreadsReadOnInteraction();
        setCommentText(event.target.value);
      }}
    />
  );
}
